import {
  ApiError,
  ProvisioningRequest,
  User,
  ResellerUser,
  CreateUserData,
  UpdateUserData,
  AccountsResponse,
  AccountDetailResponse,
  RefreshResponse,
  CancellationActionResponse,
} from '@/types';

const API_BASE = '/api';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw data as ApiError;
    }

    return data;
  }

  // Auth
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getMe(): Promise<{ user: User }> {
    return this.request('/auth/me');
  }

  // Reseller Requests
  async createRequest(payload: Record<string, unknown>): Promise<ProvisioningRequest> {
    return this.request('/requests', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateRequest(id: string, payload: Record<string, unknown>): Promise<ProvisioningRequest> {
    return this.request(`/requests/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async submitRequest(id: string): Promise<{ id: string; status: string }> {
    return this.request(`/requests/${id}/submit`, {
      method: 'POST',
    });
  }

  async getRequests(status?: string): Promise<{ requests: ProvisioningRequest[] }> {
    const params = status ? `?status=${status}` : '';
    return this.request(`/requests${params}`);
  }

  async getRequest(id: string): Promise<ProvisioningRequest> {
    return this.request(`/requests/${id}`);
  }

  // Admin
  async getAdminRequests(status?: string, search?: string): Promise<{ requests: ProvisioningRequest[] }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const queryString = params.toString();
    return this.request(`/admin/requests${queryString ? `?${queryString}` : ''}`);
  }

  async getAdminRequest(id: string): Promise<ProvisioningRequest> {
    return this.request(`/admin/requests/${id}`);
  }

  async approveRequest(id: string): Promise<{ id: string; status: string; message: string; error?: string }> {
    return this.request(`/admin/requests/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectRequest(id: string, reason?: string): Promise<{ id: string; status: string }> {
    return this.request(`/admin/requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async retryRequest(id: string): Promise<{ id: string; status: string; message: string; error?: string }> {
    return this.request(`/admin/requests/${id}/retry`, {
      method: 'POST',
    });
  }

  async completeRequest(id: string, sfAccountId: string): Promise<{ id: string; status: string; sf_account_id: string }> {
    return this.request(`/admin/requests/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ sf_account_id: sfAccountId }),
    });
  }

  async getStats(): Promise<{ stats: Record<string, number> }> {
    return this.request('/admin/stats');
  }

  // Password Reset
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  }

  // Admin User Management
  async getUsers(status?: string, search?: string): Promise<{ users: ResellerUser[] }> {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    const queryString = params.toString();
    return this.request(`/admin/users${queryString ? `?${queryString}` : ''}`);
  }

  async getUser(id: string): Promise<ResellerUser> {
    return this.request(`/admin/users/${id}`);
  }

  async createUser(data: CreateUserData): Promise<ResellerUser> {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: UpdateUserData): Promise<ResellerUser> {
    return this.request(`/admin/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateUserPassword(id: string, password: string): Promise<{ message: string }> {
    return this.request(`/admin/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  }

  async updateUserStatus(id: string, status: 'active' | 'disabled'): Promise<{ id: string; status: string; message: string }> {
    return this.request(`/admin/users/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // Salesforce Account Sync
  async getAccounts(): Promise<AccountsResponse> {
    return this.request('/accounts');
  }

  async getAccount(sfAccountId: string): Promise<AccountDetailResponse> {
    return this.request(`/accounts/${sfAccountId}`);
  }

  async refreshAccounts(): Promise<RefreshResponse> {
    return this.request('/accounts/refresh', {
      method: 'POST',
    });
  }

  // Account Lifecycle Management
  async scheduleCancellation(sfAccountId: string, endDate: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/schedule-cancellation`, {
      method: 'POST',
      body: JSON.stringify({ end_date: endDate }),
    });
  }

  async undoCancellation(sfAccountId: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/undo-cancellation`, {
      method: 'POST',
    });
  }

  async cancelTrial(sfAccountId: string, cancelDate: string): Promise<CancellationActionResponse> {
    return this.request(`/accounts/${sfAccountId}/cancel-trial`, {
      method: 'POST',
      body: JSON.stringify({ cancel_date: cancelDate }),
    });
  }
}

export const api = new ApiClient();
